import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import { CloseOutlined, DownOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Input, Popover, Tree, Typography } from 'antd';
import { useLocation } from 'react-router-dom';

import FilterItemSelected from './FilterItemSelected';
import RefreshButton from './RefreshButton';
import RequestButton from './RequestButton';
import Segments from './segments';
import DashboardSegments from '../../../../../../pages/dashboards/components/Params/filter/segments';
import { 
    ReportFilterProps, 
    FilterTreeNode, 
    ISelectedFilter,
    DISABLED_ATTR_CATEGORIES,
} from './types';
import { PagesPathnames } from '../../../../../../app/types/ad';
import { useFilterLogic } from '../../../../hooks/useFilterLogic';
import { useActions } from '../../../../hooks/useActions';
import { useTypedSelector } from '../../../../hooks/useTypedSelector';
import { migrateFiltersWithColor } from '../../../../../../helpers/utils/reports';

import '../../../../../../app/styles/components/filter-picker.scss';

/**
 * Универсальный компонент фильтров для отчётов и дашбордов
 * Объединяет функциональность UniversalFilter и DashboardFilter
 */
const ReportFilter: FC<ReportFilterProps> = ({
    reportType,
    filtersFilter,
    isCompare = false,
    withSegment = false,
    withLabel = true,
    withRefresh = false,
    disabled = false,
    disableAttr = false,
    notParams = false,
    isDashboard = false,
    isFixed = false,
    preElement,
    onUpdateDashboard,
    reqBtnDisabled = true,
    setReqBtnDisabled,
    fetchDashboardDataButtonVisible = false,
    setFetchDashboardDataButtonVisible,
}) => {
    const location = useLocation();
    const [localBtnDisabled, setLocalBtnDisabled] = useState(reqBtnDisabled);

    // Redux state
    const { filters } = useTypedSelector((state) => state.filter);
    const { isNotParamsFilterVisible } = useTypedSelector((state) => state.meta);
    const attributeState = useTypedSelector((state) => state.attributes);
    const attributeDashboardState = useTypedSelector((state) => state.attributes?.attributesDashboard);
    
    const { attribution, type_applications } = isDashboard 
        ? (attributeDashboardState || {})
        : (attributeState || {});

    // Redux actions
    const {
        setMetaCompare,
        setAllDataFetching,
        setMetaFilterUpdate,
        setAllBlocksFetching,
    } = useActions();

    // Callback для активации кнопки запроса
    const handleActivateRequest = useCallback((disable: boolean, withoutFilter = true) => {
        if (!withoutFilter) {
            setFilterVisible(disable);
        }
    }, []);

    // Хук фильтров
    const {
        treeData,
        expandedKeys,
        searchValue,
        autoExpandParent,
        filterVisible,
        setFilterVisible,
        onExpand,
        onSearch,
        onClearFilters,
        onLoadData,
    } = useFilterLogic({
        reportType,
        filtersFilter,
        disableAttr,
        isDashboard,
        isFixed,
        onActivateRequest: handleActivateRequest,
    });

    // Управление состоянием кнопки
    const handleSetBtnDisabled = useCallback((val: boolean) => {
        setLocalBtnDisabled(val);
        setReqBtnDisabled?.(val);
        setFetchDashboardDataButtonVisible?.(val);
    }, [setReqBtnDisabled, setFetchDashboardDataButtonVisible]);

    // Сброс режима сравнения
    useEffect(() => {
        if (!isCompare && !notParams) {
            setMetaCompare({ value: false });
        }
    }, [isCompare, notParams, setMetaCompare]);

    // Скрытие поповера при изменении фильтров
    useEffect(() => {
        setFilterVisible(false);
    }, [filters, setFilterVisible]);

    // Активация кнопки при изменении фильтров/атрибуции
    useEffect(() => {
        if (isDashboard) {
            handleSetBtnDisabled(false);
        }
    }, [filters, attribution, type_applications, isDashboard, handleSetBtnDisabled]);

    // Обновление данных дашборда
    const handleUpdateDashboardData = useCallback(() => {
        if (isDashboard && onUpdateDashboard) {
            setAllBlocksFetching({ value: true });
            setAllDataFetching({ value: true });
            setTimeout(() => {
                setAllBlocksFetching({ value: false });
                setMetaFilterUpdate({ value: false });
            }, 2000);
            onUpdateDashboard();
        }
    }, [isDashboard, onUpdateDashboard, setAllBlocksFetching, setAllDataFetching, setMetaFilterUpdate]);

    // Очистка фильтров с активацией кнопки
    const handleClearFilters = useCallback(() => {
        onClearFilters();
        if (isDashboard) {
            handleSetBtnDisabled(false);
        }
    }, [onClearFilters, isDashboard, handleSetBtnDisabled]);

    // Проверка открытия модального окна фильтра
    const shouldOpenFilterModal = useCallback((node: FilterTreeNode) => {
        if (!node.isLeaf) return false;
        if (typeof node.onOk !== 'function') return false;
        return true;
    }, []);

    // Определение классов для разных вкладок
    const tabClassNames = useMemo(() => {
        const isAutoRKTab = location.pathname === PagesPathnames.autoPlacement &&
            (Number(new URLSearchParams(location.search).get('tab')) === 1 ||
                !new URLSearchParams(location.search).get('tab'));
        const isAutoAdTab = location.pathname === PagesPathnames.autoPlacement &&
            Number(new URLSearchParams(location.search).get('tab')) === 2;
        const isManualAdTab = location.pathname === PagesPathnames.manualPlacement &&
            Number(new URLSearchParams(location.search).get('tab')) === 1;

        return {
            isAutoRKTab,
            isAutoAdTab,
            isManualAdTab,
        };
    }, [location.pathname, location.search]);

    // Мигрируем фильтры для корректного отображения (добавляем color если отсутствует)
    const migratedFilters = useMemo(() => migrateFiltersWithColor(filters as ISelectedFilter[]), [filters]);

    // Группируем фильтры: все исключения одной сущности (по цвету) объединяем в один блок
    const filterRender = useMemo(() => {
        // Сначала подготовим список фильтров с их индексами, пропуская пустые
        const validFilters: { item: ISelectedFilter; index: number }[] = [];
        (migratedFilters as ISelectedFilter[]).forEach((item, index) => {
            if (item.type === 'datetimerange' && !item.start && !item.end) {
                return;
            }
            validFilters.push({ item, index });
        });

        // Группируем ВСЕ исключения с одинаковым цветом в один блок
        const excludesByColor: Map<string, { item: ISelectedFilter; index: number }[]> = new Map();
        const regularFilters: { item: ISelectedFilter; index: number }[] = [];
        
        validFilters.forEach(({ item, index }) => {
            const isExclude = item.id?.includes('not_');
            const color = item.color || null;
            
            if (isExclude && color) {
                if (!excludesByColor.has(color)) {
                    excludesByColor.set(color, []);
                }
                excludesByColor.get(color)!.push({ item, index });
            } else {
                regularFilters.push({ item, index });
            }
        });

        // Формируем итоговый список: сначала обычные фильтры, потом группы исключений по цветам
        const result: React.ReactNode[] = [];
        let isFirst = true;

        // Рендерим обычные фильтры
        regularFilters.forEach(({ item, index: itemIndex }) => {
            result.push(
                <Fragment key={`regular-${itemIndex}`}>
                    {!isFirst && (
                        <Typography.Text type="secondary">
                            {isDashboard ? <>&nbsp;и&nbsp;</> : 'и'}
                        </Typography.Text>
                    )}
                    <FilterItemSelected
                        arrayIndex={itemIndex}
                        itemData={item}
                        full_name={item.full_name}
                        {...item}
                    />
                </Fragment>
            );
            isFirst = false;
        });

        // Рендерим группы исключений по цветам
        excludesByColor.forEach((items, color) => {
            result.push(
                <Fragment key={`exclude-${color}`}>
                    {!isFirst && (
                        <Typography.Text type="secondary">
                            {isDashboard ? <>&nbsp;и&nbsp;</> : 'и'}
                        </Typography.Text>
                    )}
                    <span style={{ display: 'inline-flex', alignItems: 'flex-start' }}>
                        <b className="filter-exclude-prefix" style={{ marginRight: 4, marginLeft: 4, alignSelf: 'center' }}>
                            НЕ{isDashboard ? <>&nbsp;</> : null}
                        </b>
                        <span 
                            style={{ 
                                display: 'inline-flex', 
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                padding: '2px',
                                gap: '4px',
                                border: '2px solid #ff4d4f',
                                borderRadius: '8px',
                            }}
                        >
                            {items.map(({ item, index: itemIndex }, i) => (
                                <Fragment key={`${item.id}-${itemIndex}`}>
                                    {i !== 0 && (
                                        <Typography.Text type="secondary" style={{ margin: '0 4px' }}>
                                            {isDashboard ? <>&nbsp;и&nbsp;</> : 'и'}
                                        </Typography.Text>
                                    )}
                                    <FilterItemSelected
                                        arrayIndex={itemIndex}
                                        itemData={item}
                                        full_name={item.full_name}
                                        {...item}
                                    />
                                </Fragment>
                            ))}
                        </span>
                    </span>
                </Fragment>
            );
            isFirst = false;
        });

        return result;
    }, [migratedFilters, isDashboard]);

    // Фильтрация дерева для disableAttr
    const filteredTreeData = useMemo(() => {
        if (disableAttr) {
            return treeData.filter(
                (item) => !DISABLED_ATTR_CATEGORIES.includes(item.name)
            );
        }
        return treeData;
    }, [treeData, disableAttr]);

    // CSS классы контейнера
    const containerClassName = useMemo(() => {
        const classes = ['params__filter'];
        if (tabClassNames.isAutoRKTab) classes.push('filter-auto-rk-tab');
        if (tabClassNames.isAutoAdTab) classes.push('filter-auto-ad-tab');
        if (tabClassNames.isManualAdTab) classes.push('filter-manual-ad-tab');
        if (isFixed) classes.push('filter-fixed-block');
        return classes.join(' ');
    }, [tabClassNames, isFixed]);

    // Стили контейнера
    const containerStyle = useMemo(() => ({
        paddingBottom: filters?.length > 0 ? '0px' : '20px',
        ...(isDashboard && filters.length > 0 ? { flexWrap: 'wrap' as const } : {}),
    }), [filters?.length, isDashboard]);

    // Проверка видимости контента поповера
    const isPopoverContentVisible = notParams || !isNotParamsFilterVisible || isDashboard;

    return (
        <div className={containerClassName} style={containerStyle}>
            {/* Элемент перед фильтрами (для дашборда) */}
            {preElement}

            {/* Сегменты */}
            {withSegment && (
                <div>
                    {isDashboard ? (
                        <DashboardSegments disabled={disabled} />
                    ) : (
                        <Segments disabled={disabled} setBtnDisabled={handleActivateRequest} />
                    )}
                </div>
            )}

            {/* Кнопка обновления */}
            {withRefresh && <RefreshButton />}

            {/* Кнопка очистки фильтров */}
            {filters?.length > 0 && (
                <Button
                    className="filter-clear-btn"
                    onClick={handleClearFilters}
                    icon={<CloseOutlined />}
                    title="Очистить"
                />
            )}

            <div className={`filter-content ${withSegment ? 'filter-content--with-segment' : ''}`}>
                {/* Лейбл */}
                {withLabel && (
                    <Typography.Text 
                        type="secondary" 
                        className={isDashboard ? 'filter-label filter-label--dashboard' : 'filter-label'}
                    >
                        Условия фильтра:&nbsp;
                    </Typography.Text>
                )}

                {/* Выбранные фильтры */}
                {filters?.length !== 0 && filterRender}

                {/* Поповер с деревом фильтров */}
                <Popover
                    content={
                        isPopoverContentVisible && (
                            <Tree.DirectoryTree
                                blockNode
                                switcherIcon={<DownOutlined />}
                                showIcon={false}
                                loadData={onLoadData}
                                selectable={!isDashboard}
                                onSelect={(_keys, info) => {
                                    if (isDashboard) return;
                                    const node = info.node as unknown as FilterTreeNode;
                                    if (!shouldOpenFilterModal(node)) return;
                                    node.onOk?.(false);
                                }}
                                treeData={filteredTreeData}
                                onExpand={(keys) => onExpand(keys as string[])}
                                expandedKeys={expandedKeys}
                                autoExpandParent={autoExpandParent}
                                key="name"
                            />
                        )
                    }
                    title={
                        isPopoverContentVisible && (
                            <Input
                                onChange={onSearch}
                                placeholder="Найти условие"
                                allowClear
                                value={searchValue}
                            />
                        )
                    }
                    trigger="click"
                    placement="bottom"
                    overlayClassName="filter-popover"
                    open={filterVisible}
                    onOpenChange={setFilterVisible}
                >
                    <Button icon={<PlusOutlined />} type="primary" shape="circle" />
                </Popover>

                {/* Кнопка запроса */}
                {isDashboard ? (
                    <RequestButton
                        setUpdateDashboard={handleUpdateDashboardData}
                        btnDisabled={localBtnDisabled}
                        setBtnDisabled={handleSetBtnDisabled}
                    />
                ) : (
                    !notParams && setReqBtnDisabled && (
                        <RequestButton
                            setUpdateDashboard={handleUpdateDashboardData}
                            btnDisabled={reqBtnDisabled}
                            setBtnDisabled={setReqBtnDisabled}
                        />
                    )
                )}
            </div>
        </div>
    );
};

export default ReportFilter;
